import React, { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";

const THRESHOLD = 70;
const MAX_PULL = 110;
const RESUME_AFTER_MS = 30_000;

function scrolledInside(el) {
  for (let n = el; n && n !== document.body; n = n.parentElement) {
    if (n.scrollTop > 0) return true;
  }
  return false;
}

export default function PullToRefresh() {
  const qc = useQueryClient();
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const start = useRef(null);
  const pullRef = useRef(0);

  const refresh = async () => {
    setRefreshing(true);
    await Promise.all([qc.invalidateQueries(), new Promise((r) => setTimeout(r, 600))]);
    setRefreshing(false);
  };

  useEffect(() => {
    const onStart = (e) => {
      const blocked = window.scrollY > 0 || document.querySelector("[role=dialog]") || scrolledInside(e.target);
      start.current = blocked || e.touches.length > 1 ? null : e.touches[0].clientY;
    };
    const onMove = (e) => {
      if (start.current == null) return;
      const dy = e.touches[0].clientY - start.current;
      if (dy <= 0 || window.scrollY > 0) { pullRef.current = 0; setPull(0); return; }
      pullRef.current = Math.min(dy * 0.5, MAX_PULL);
      setPull(pullRef.current);
    };
    const onEnd = () => {
      if (pullRef.current >= THRESHOLD) refresh();
      start.current = null; pullRef.current = 0; setPull(0);
    };
    let hiddenAt = null;
    const onVisibility = () => {
      if (document.visibilityState === "hidden") { hiddenAt = Date.now(); return; }
      if (hiddenAt && Date.now() - hiddenAt > RESUME_AFTER_MS) qc.invalidateQueries();
      hiddenAt = null;
    };
    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onEnd);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!pull && !refreshing) return null;
  const ready = pull >= THRESHOLD;
  const offset = refreshing ? 56 : pull;
  return (
    <div
      data-testid="pull-to-refresh-indicator"
      className="lg:hidden fixed left-1/2 z-50 -translate-x-1/2 flex items-center gap-2 rounded-full bg-white shadow-md border border-stone-200 px-3 py-1.5 text-xs text-[#1C352D]"
      style={{ top: `calc(env(safe-area-inset-top, 0px) + ${offset}px)`, transition: pull ? "none" : "top 200ms ease" }}
    >
      <RefreshCw
        size={14}
        className={refreshing ? "animate-spin text-[#E2725B]" : ready ? "text-[#E2725B]" : "text-stone-400"}
        style={refreshing ? undefined : { transform: `rotate(${pull * 3}deg)` }}
      />
      {refreshing ? "Aggiornamento…" : ready ? "Rilascia per aggiornare" : "Trascina per aggiornare"}
    </div>
  );
}
